import { useEffect } from 'react'
import { GAME_TYPES, isNassauOnly } from '../../constants/gameTypes'

/**
 * Format (Nassau/Standard) + Nassau weight presets selector.
 * Shared between RoundSetupWizard (StepFormatAndWager) and SessionSetupWizard (StepFormat).
 *
 * Props:
 *   matchFormat, setMatchFormat, nassauWeights, setNassauWeights,
 *   numHoles, gameTypeId
 */
export default function FormatSelector({ matchFormat, setMatchFormat, nassauWeights, setNassauWeights, numHoles, gameTypeId }) {
  const gt = gameTypeId ? GAME_TYPES.find(g => g.id === gameTypeId) : null
  const isPerNine = gt?.scoring === 'per_nine'
  const is9Holes = numHoles === 9
  const nassauDisabled = is9Holes
  const standardDisabled = isNassauOnly(gameTypeId) || isPerNine

  // Auto-select format when constraints change
  useEffect(() => {
    if (nassauDisabled && matchFormat === 'nassau') {
      setMatchFormat('standard')
    } else if (standardDisabled && matchFormat === 'standard') {
      setMatchFormat('nassau')
    }
  }, [gameTypeId, numHoles])

  // Per-nine games don't need this step's format selector
  if (isPerNine) {
    return (
      <div className="form-section">
        <h3>Match Format</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', lineHeight: 1.5 }}>
          This game uses per-nine format (front 9 and back 9 as separate matches).
        </p>
      </div>
    )
  }

  const formats = [
    {
      id: 'nassau',
      label: 'Nassau',
      description: '3 separate segments: Front 9, Back 9, and Overall 18 holes. The classic format for matches with your friends.',
      disabled: nassauDisabled,
      disabledNote: '(18-hole rounds only)',
    },
    {
      id: 'standard',
      label: 'Standard',
      description: '1 point for the winner of the match',
      disabled: standardDisabled,
      disabledNote: '(Not available for this game)',
    },
  ]

  return (
    <div className="form-section">
      <h3>Match Format</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {formats.map(fmt => {
          const selected = matchFormat === fmt.id
          const disabled = fmt.disabled
          const isNassauCard = fmt.id === 'nassau'
          return (
            <div
              key={fmt.id}
              onClick={() => !disabled && setMatchFormat(fmt.id)}
              style={{
                padding: '0.75rem',
                backgroundColor: selected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
                borderRadius: '0.5rem',
                border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%',
                  border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {selected && (
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'var(--accent-primary)' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {fmt.label}
                    {disabled && <span style={{ fontWeight: '400', color: 'var(--text-muted)', marginLeft: '0.375rem', fontSize: '0.75rem' }}>{fmt.disabledNote}</span>}
                  </span>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                    {fmt.description}
                  </div>
                </div>
              </div>
              {isNassauCard && selected && (
                <div style={{ marginTop: '0.625rem', paddingLeft: '2.125rem' }} onClick={e => e.stopPropagation()}>
                  <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>Segment Weighting</label>
                  <div style={{ display: 'flex', gap: '0.375rem' }}>
                    {[
                      { weights: [1, 1, 1], label: 'Standard 1-1-1', desc: '1 point each for front / back / overall' },
                      { weights: [1, 1, 2], label: '2x Overall 1-1-2', desc: '1 point front & back, 2 points overall' },
                    ].map(opt => {
                      const active = nassauWeights[0] === opt.weights[0] && nassauWeights[1] === opt.weights[1] && nassauWeights[2] === opt.weights[2]
                      return (
                        <div
                          key={opt.label}
                          onClick={() => setNassauWeights(opt.weights)}
                          style={{
                            flex: 1,
                            padding: '0.375rem 0.5rem',
                            borderRadius: '0.375rem',
                            border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                            backgroundColor: active ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)' : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: '0.75rem', fontWeight: '600' }}>{opt.label}</div>
                          <div style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', marginTop: '0.0625rem' }}>{opt.desc}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
